import { useCallback, useRef, useState } from "react";
import type { OverlayPanel } from "primereact/overlaypanel";

export const overlayMenuItemProps = {
  role: "menuitem" as const,
  tabIndex: -1,
};

export const useOverlayMenuKeyboard = () => {
  const overlayRef = useRef<OverlayPanel>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const pendingFocusRef = useRef<number | null>(null);

  const getMenuItems = useCallback(() => {
    if (!menuRef.current) return [];
    return Array.from(
      menuRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]'),
    );
  }, []);

  const focusMenuItem = useCallback(
    (index: number) => {
      const items = getMenuItems();
      if (items.length === 0) return;
      const safeIndex = ((index % items.length) + items.length) % items.length;
      items[safeIndex]?.focus();
    },
    [getMenuItems],
  );

  const rememberTrigger = useCallback((element: HTMLButtonElement | null) => {
    triggerRef.current = element;
  }, []);

  const closeMenu = useCallback(() => {
    overlayRef.current?.hide();
    triggerRef.current?.focus();
  }, []);

  const onShow = useCallback(() => {
    setIsOpen(true);
    if (pendingFocusRef.current !== null) {
      const index = pendingFocusRef.current;
      pendingFocusRef.current = null;
      requestAnimationFrame(() => focusMenuItem(index));
    }
  }, [focusMenuItem]);

  const onHide = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      rememberTrigger(e.currentTarget);

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          pendingFocusRef.current = 0;
          if (isOpen) {
            focusMenuItem(0);
          } else {
            overlayRef.current?.show(e as never, e.currentTarget);
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          pendingFocusRef.current = -1;
          if (isOpen) {
            focusMenuItem(-1);
          } else {
            overlayRef.current?.show(e as never, e.currentTarget);
          }
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (isOpen) {
            overlayRef.current?.hide();
          } else {
            pendingFocusRef.current = 0;
            overlayRef.current?.show(e as never, e.currentTarget);
          }
          break;
        case "Escape":
          if (isOpen) {
            e.preventDefault();
            e.stopPropagation();
            closeMenu();
          }
          break;
      }
    },
    [isOpen, focusMenuItem, closeMenu, rememberTrigger],
  );

  const onMenuKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const items = getMenuItems();
      const currentIndex = items.indexOf(e.target as HTMLElement);

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          focusMenuItem(currentIndex + 1);
          break;
        case "ArrowUp":
          e.preventDefault();
          focusMenuItem(currentIndex - 1);
          break;
        case "Home":
          e.preventDefault();
          focusMenuItem(0);
          break;
        case "End":
          e.preventDefault();
          focusMenuItem(items.length - 1);
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          closeMenu();
          break;
        case "Tab":
          e.preventDefault();
          closeMenu();
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          (e.target as HTMLElement).click();
          closeMenu();
          break;
      }
    },
    [getMenuItems, focusMenuItem, closeMenu],
  );

  return {
    overlayRef,
    triggerRef,
    menuRef,
    isOpen,
    rememberTrigger,
    onShow,
    onHide,
    onTriggerKeyDown,
    onMenuKeyDown,
    closeMenu,
  };
};
